import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Flame, Menu, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/studio/halftone", label: "Halftone" },
  { to: "/studio/vector", label: "AI Vector" },
  { to: "/studio/watermark", label: "Watermark" },
  { to: "/studio/upgrade", label: "Photo Upgrader" },
  { to: "/pricing", label: "Pricing" },
  { to: "/services", label: "Services" },
  { to: "/learn", label: "Learn" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { user, usage, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  const initials = React.useMemo(() => {
    if (!user) return "";
    const n = user.name || user.email;
    return n.slice(0, 2).toUpperCase();
  }, [user]);

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-3 focusable">
          <img src="/brand/fdl-logo.png" alt="Furnace Design Lab" className="h-10 w-auto drop-shadow-[0_0_18px_rgba(74,164,255,0.35)]" />
          <div className="hidden leading-tight sm:block">
            <div className="brand-font text-[13px] font-extrabold uppercase tracking-widest">
              <span className="chrome-text">Furnace Design</span>{" "}<span className="ember-text">Lab</span>
            </div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">forge · press · wear</div>
          </div>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {NAV.map((n) => {
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`focusable rounded-md px-3 py-2 text-sm transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {user && usage && (
            <span className="chip hidden sm:inline-flex">
              <span className="relative inline-block h-2 w-2 rounded-full bg-[#4aa4ff] pulse-dot"></span>
              {usage.pro ? "Pro · Unlimited" : `${usage.remaining} free exports left`}
            </span>
          )}

          {!user ? (
            <>
              <Button variant="ghost" className="hidden sm:inline-flex" onClick={() => navigate("/login")}>Sign in</Button>
              <Button className="btn-ember font-semibold" onClick={() => navigate("/signup")}>Start Free</Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger className="focusable rounded-full">
                <Avatar className="h-9 w-9 border border-border">
                  {user.picture ? <AvatarImage src={user.picture} /> : null}
                  <AvatarFallback className="bg-secondary text-foreground">{initials}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user.name || "Account"}</div>
                  <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")}>Dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/studio/halftone")}>Halftone Studio</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/studio/vector")}>AI Vector Creator</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/studio/watermark")}>Watermark Remover</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/studio/upgrade")}>Photo Upgrader</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/redeem")}>Redeem Pro Code</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }}>Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <button className="focusable lg:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background/95 lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col p-4">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="rounded-md px-3 py-3 text-sm hover:bg-secondary/60">
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
